import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import ControlledDatePicker from 'src/common/components/form/controlled/ControlledDatePicker';
import { IParticipant } from 'src/interfaces/Participant';

interface Props {
  handleSave: (email: string, dateOfBirth: Date | null) => void;
  handleClose: () => void;
  selectedParticipant?: IParticipant;
}

const transferSchema = yup.object().shape({
  email: yup.string().email().required(),
  dateOfBirth: yup.date().required().nullable(),
});

interface TransferFormData {
  email: string;
  dateOfBirth: Date | null;
}

const TransferForm = ({ handleSave, handleClose, selectedParticipant }: Props) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<TransferFormData>({
    resolver: yupResolver(transferSchema),
    defaultValues: {
      email: '',
      dateOfBirth: null,
    },
  });

  const onSubmit = (data: TransferFormData) => {
    handleSave(data.email, data.dateOfBirth);
    reset();
  };

  const handleCancel = () => {
    reset();
    handleClose();
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {selectedParticipant && (
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('participants.transferParticipantDescription', {
            name: `${selectedParticipant.lastName} ${selectedParticipant.firstName}`,
          })}
        </Typography>
      )}

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('participants.dobRequiredForTransfer')}
      </Typography>

      <TextField
        {...register('email')}
        autoFocus
        margin="dense"
        label={t('participants.enterTransferEmail')}
        type="email"
        fullWidth
        variant="outlined"
        error={!!errors.email}
        helperText={errors.email?.message}
        sx={{ mb: 2 }}
      />

      <ControlledDatePicker
        name="dateOfBirth"
        control={control}
        label={t('participants.dateOfBirth')}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={handleCancel} color="inherit">
          {t('base.cancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {t('participants.transfer')}
        </Button>
      </Box>
    </Box>
  );
};

export default TransferForm;
